import { useQuery } from "@apollo/client";
import { AssetRemoveEvents } from "../graphql/queries/assetRemoveEvents";
import { isContaminationDeclarationsEnabled } from "../utils/contaminationDeclarationsEnabled";
import { useDeclarationForEvent } from "./useDeclarationForEvent";

/**
 * Whether the asset's "Contamination Declarations" nav entry is worth showing:
 * the newest ASSET_REMOVE event of the asset either already has a declaration
 * or may start one (`contaminationDeclarationAvailability`, region-gated).
 * Without a removal, or outside the regions the form is offered for, the
 * section could only show removals nobody can act on.
 *
 * Only the newest removal is checked — the backend has no per-asset
 * declaration list or count. A declaration on an older removal while the
 * newest one is not available stays reachable through the event history.
 *
 * False while loading and on error, so the entry appears late rather than
 * offering a section the server then answers with nothing.
 */
export const useAssetContaminationDeclarationsVisible = (assetId: string | null | undefined): boolean => {
    const enabled = isContaminationDeclarationsEnabled();
    const removeEvents = useQuery(AssetRemoveEvents, {
        variables: { assetId, limit: 1 },
        skip: !enabled || !assetId,
    });
    const latestRemoveEventId: string | undefined = removeEvents.data?.installedBaseEventList?.installedBaseEvents?.[0]?.id;
    const { declaration, available, loading, error } = useDeclarationForEvent(latestRemoveEventId, !enabled);

    return enabled && !!latestRemoveEventId && !loading && !error && (!!declaration || available);
};
